import * as ents from '@attain-apps/entities'
import { Multirange, Range } from '@attain-apps/range'
import * as dateFns from 'date-fns'

export namespace DateMultirange {
  /**
   * Converts persisted date ranges into local, inclusive calendar ranges for
   * Angular Material.
   *
   * PostgreSQL canonicalizes discrete date ranges to inclusive starts and
   * exclusive ends. The date picker instead represents the dates selected by
   * the user as inclusive endpoints, so exclusive persisted boundaries must be
   * moved to the first or last included calendar day.
   */
  export function normalizeToLocal(
    multirange: Readonly<Multirange<Date>> | undefined,
  ): Multirange<Date> | undefined {
    if (!multirange) {
      return
    }

    return new Multirange<Date>({
      ranges: multirange.ranges.map(_normalizeRangeToLocal),
    })
  }

  /**
   * Converts inclusive date-picker ranges to UTC midnight before they are
   * emitted and serialized.
   */
  export function normalizeToUtc(
    multirange: Readonly<Multirange<Date>> | undefined,
  ): Multirange<Date> | undefined {
    if (!multirange) {
      return
    }

    return new Multirange<Date>({
      ranges: multirange.ranges.map(_normalizeRangeToUtc),
    })
  }

  /** Converts a persisted range to the inclusive representation used by the UI. */
  function _normalizeRangeToLocal(range: Readonly<Range<Date>>): Range<Date> {
    const normalizedStart = ents.core.display.date.normalizeToLocal(range.start)
    const normalizedEnd = ents.core.display.date.normalizeToLocal(range.end)

    return new Range<Date>({
      end:
        normalizedEnd && !range.includesEnd
          ? dateFns.addDays(normalizedEnd, -1)
          : normalizedEnd,
      start:
        normalizedStart && !range.includesStart
          ? dateFns.addDays(normalizedStart, 1)
          : normalizedStart,
    })
  }

  /** Converts an inclusive UI range to UTC midnight for serialization. */
  function _normalizeRangeToUtc(range: Readonly<Range<Date>>): Range<Date> {
    return new Range<Date>({
      end: ents.core.display.date.normalizeToUtc(range.end),
      start: ents.core.display.date.normalizeToUtc(range.start),
    })
  }
}
